import { parseErrorMessage } from "#src/util/helpers.ts";
import { type Reactive, reactive } from "vue";

export interface SavablePropertyRule<T, P = null> {
  v: (this: SavableProperty<T, P>) => boolean;
  message: string | ((this: SavableProperty<T, P>) => string);
}

interface SavablePropertyArgs<T, P, C> {
  rules: Record<string, SavablePropertyRule<T, P>>;
  eachRules: Record<string, SavablePropertyRule<C, T>>;
  requestMap: SavableProperty<T, P>["requestMap"];
  group: SavableProperty<T, P>["group"];
  initialValue: SavableProperty<T, P>["model"];
  requestFormatter: (v: T | null) => any;
  validationSideEffects?: () => void;
}

export function assertSavableProperty(
  v: any
): asserts v is SavableProperty<any> {
  if (!v.IS_SAVABLE_PROPERTY) throw "Not savable property";
}

export interface SavableProperty<T, P = any, C = any> {
  IS_SAVABLE_PROPERTY: true;
  success: boolean | null;
  _internalValue: T | null;
  errorMessages: string[];
  dirty: boolean;
  validate: (markDirty?: boolean, isOptional?: boolean) => boolean;
  errorMessage: string | null;
  requests: PendingRequest[];
  requestMap: string | null;
  group: string | null;
  format(): any;
  model: T | null;
  deleteRequest: (uuid: string) => number;
  load: (v: T | null) => void;
  _externallyValid: boolean | null;
  externallyValid: boolean | null;
  getChildProperty(index: number): SavableProperty<C, T>;
  parent?: SavableProperty<P>;
  index?: number;
  rules: Record<string, SavablePropertyRule<T, P>>;
}

interface PendingRequest {
  id: string;
  initiatedAt: number;
  payload: string;
}

export function useSavableProperty<T, P = any, C = any>(
  args: Partial<SavablePropertyArgs<T, P, C>> = {}
): SavableProperty<T, P> {
  const {
    initialValue,
    requestMap,
    group,
    requestFormatter,
    validationSideEffects,
    eachRules,
  } = args;

  let _internalValue = null;
  let success = null;
  if (initialValue || initialValue === 0) {
    _internalValue = initialValue;
    success = true; // TODO(tim) we should probably validate against the rules
  }
  return {
    IS_SAVABLE_PROPERTY: true,
    success,
    _internalValue,
    errorMessages: [],
    dirty: false,
    rules: args.rules || {},
    validate(markDirty = false, isOptional = false) {
      this.errorMessages.splice(0, this.errorMessages.length);

      if (this.rules) {
        Object.keys(this.rules).forEach((rule: string) => {
          if (!this.rules) return;

          try {
            if (isOptional) {
              if (typeof this.model === "string" && this.model === "") return;
              if (this.model === null) return;
            }

            if (this.rules[rule].v.bind(this)()) return;

            let message = this.rules[rule].message;
            if (typeof message === "function") {
              message = message.bind(this)();
            }

            this.errorMessages.push(message as string);
          } catch (e) {
            console.error(e);
            this.errorMessages.push("error");
          }
        });
      }

      if (this.errorMessage) {
        this.errorMessages.push(this.errorMessage);
      }

      this.success = !this.errorMessages.length;
      if (markDirty) this.dirty = true;
      return this.success === true;
    },
    requests: [],
    errorMessage: "",
    requestMap: requestMap || null,
    group: group || null,
    format() {
      let reqValue = this.model;

      if (requestFormatter) reqValue = requestFormatter(this.model);

      return reqValue;
    },
    deleteRequest(uuid) {
      const index = this.requests.findIndex(({ id }) => id === uuid);
      if (index > -1) this.requests.splice(index, 1);
      return index;
    },
    load(v) {
      this._externallyValid = true;
      this.model = v;
      this.dirty = false;
    },

    get model() {
      return this._internalValue;
    },
    set model(v) {
      this._internalValue = v;
      this.errorMessage = "";
      this.validate();
      if (validationSideEffects) validationSideEffects();
    },
    _externallyValid: null,
    get externallyValid() {
      return this._externallyValid;
    },
    set externallyValid(v) {
      this._externallyValid = v;
      this.validate();
    },
    getChildProperty(index: number) {
      if (!Array.isArray(this.model))
        throw "getChildProperty is only supported for array types";
      const property = useSavableProperty<C, T>({
        group,
        requestMap,
        initialValue: this.model[index],
      });
      if (eachRules) {
        property.rules = eachRules;
      }

      Object.defineProperty(property, "model", {
        get() {
          return this._internalValue;
        },
        set(v) {
          this._internalValue = v;
          this.errorMessage = "";
          this.validate();
          this.parent.model[this.index] = v;
        },
      });

      property.parent = this;
      property.index = index;

      if (requestFormatter) {
        Object.defineProperty(property, "requestMap", () => {
          if (!this.parent) throw "Parent not set on child property";
          return this.parent.format();
        });
      }

      return property;
    },
  };
}

// This is useful if we need the returnType of a savableProperty needs to line up with pinia's storeToRefs
//  -> pretty much any time we use it in a non pinia stateful fashion.

// should we always just return a reactive value (even in pinia stores)? why would we use reactive instead of nested refs?
export function useInComponentSavableProperty<T, P, C>(
  args: Partial<SavablePropertyArgs<T, P, C>>
): Reactive<SavableProperty<T, P, C>> {
  return reactive(useSavableProperty<T, P, C>(args));
}

export function useStoreSavableProperty<T, P = null, C = null>(
  args: Partial<SavablePropertyArgs<T, P, C>>
): Reactive<SavableProperty<T, P, C>> {
  return reactive(useSavableProperty<T, P, C>(args));
}

interface SavablePropertyRequestWrapperData<T, P, C> {
  composableAttributes: SavableProperty<T, P, C>[];
  body: object;
  uuid: string;
}

export async function savablePropertyRequestWrapper<
  R,
  T = any,
  P = any,
  C = any,
>(
  func: () => Promise<R | undefined>,
  options: SavablePropertyRequestWrapperData<T, P, C>
): Promise<R | undefined> {
  options.composableAttributes.forEach(a => {
    a.requests.push({
      id: options.uuid,
      initiatedAt: new Date().getTime(),
      payload: JSON.stringify(options.body),
    });
  });
  let errorMessage: string, response, error;
  try {
    response = await func();
  } catch (e: any) {
    error = e;
    errorMessage = parseErrorMessage(e);
  } finally {
    options.composableAttributes.forEach(a => {
      const index = a.deleteRequest(options.uuid);
      if (index > -1) a.requests.splice(index, 1);
      if (errorMessage) a.errorMessage = errorMessage;

      a.externallyValid = !errorMessage;
      if (errorMessage) a.errorMessage = errorMessage;
      a.validate();
    });
  }

  if (error) throw error;
  return response;
}
